import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Box, Button, Checkbox, Divider, DrawerBody, DrawerCloseButton, DrawerFooter, HStack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../../../../../../../constants/cypressIds';
import { hideModal, showModal } from '../../../../../../../../../../redux/modules/modal';
import { hideTray } from '../../../../../../../../../../redux/modules/tray';
import colors from '../../../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../../../types/RequirementTemplateBlueprintType';
import DeleteModalBody from '../../../../../../../../../DeleteModalBody';
import FormInput from '../../../../../../../../../form/FormInput';
import InputLabel from '../../../../../../../../../form/InputLabel';
import PINSAIAssistantLabel from '../../../../../../../../../PINSAIAssistantLabel';
import SearchInputGroup from '../../../../../../../../../SearchInputGroup';

const QuestionLinkTray = ({ blueprint, question, currentRequirementTemplateBlocks }) => {
  const dispatch = useDispatch();
  const useFormMethods = useForm({
    defaultValues: {
      question_name: question.name,
      question_question: question.question,
      question_red_flag: question.redFlag
    }
  });
  const { handleSubmit } = useFormMethods;

  const blocks = useMemo(() => currentRequirementTemplateBlocks.reduce((acc, block) => {
    const blockId = block.id;
    const { requirementTemplateBlockProperties } = block;
    const firstProperty = requirementTemplateBlockProperties[0];

    if (firstProperty.requirementType === 'insurance_language') {
      return acc;
    }

    const blockSelected = question.requirementTemplateQuestions.find(rTQ => rTQ.requirementTemplateQuestionBlocks.find(rTQB => rTQB.requirementTemplateBlockId === blockId));

    return [
      ...acc,
      {
        ...block,
        isSelected: blockSelected
      }
    ];
  }, []), [currentRequirementTemplateBlocks, question]);

  const [fixedBlocks, setFixedBlocks] = useState(blocks);

  const checkAllText = useMemo(() => {
    if (!fixedBlocks && !fixedBlocks?.length) {
      return '';
    }

    if (fixedBlocks.every(block => block.isSelected)) {
      return 'Uncheck All';
    }

    return 'Check All';
  }, [fixedBlocks]);

  const handleCheckboxChange = (blockId, isChecked) => {
    const updatedBlocks = fixedBlocks.map(block => {
      if (block.id === blockId) {
        return { ...block, isSelected: isChecked };
      }

      return block;
    });
    setFixedBlocks(updatedBlocks);
  };

  const handleCheckAll = isChecked => {
    const updatedBlocks = fixedBlocks.map(block => ({ ...block, isSelected: isChecked }));

    setFixedBlocks(updatedBlocks);
  };

  const onSearchChange = e => {
    const searchValue = e.target.value;
    const filteredBlocks = blocks.filter(block => {
      const { requirementTemplateBlockProperties } = block;
      const firstProperty = requirementTemplateBlockProperties[0];
      return firstProperty.requirement.displayName.toLowerCase().includes(searchValue.toLowerCase());
    });
    setFixedBlocks(filteredBlocks);
  };

  const onSubmit = data => {
    const selectedBlocks = fixedBlocks.filter(block => block.isSelected);

    if (!selectedBlocks.length) {
      dispatch(showModal(() => (
        <DeleteModalBody
          deleteMessage="You have not selected any related requirements. Are you sure you want to remove this question from your template?"
          primaryButtonLabel="Remove"
          primaryOnClick={async () => {
            dispatch(blueprint.deleteQuestionRequirement(question.id));
            dispatch(hideModal());
            dispatch(hideTray());
          }}
        />
      ), {
        title: 'Remove Question?',
        size: 'sm'
      }));

      return;
    }

    const requirementTemplateBlockIds = selectedBlocks.map(block => block.id);

    dispatch(blueprint.createQuestionRequirement(question, requirementTemplateBlockIds));
    dispatch(hideTray());
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      h="100%"
      m={6}
      overflowY="auto"
    >
      <DrawerCloseButton />
      <PINSAIAssistantLabel mb={4} w="240px" />
      <Text
        color="greyScale.900"
        fontSize="20px"
        mb={4}
        textStyle="displayXxs"
        whiteSpace="pre-line"
      >
        Link Question to Requirements
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerBody px={0}>
          <FormInput
            isDisabled
            inputName="question_name"
            labelName="Name"
            useFormMethods={useFormMethods}
          />
          <FormInput
            isDisabled
            inputName="question_question"
            labelName="Question"
            type="textArea"
            useFormMethods={useFormMethods}
          />

          <FormInput
            isDisabled
            inputName="question_red_flag"
            labelName="Red Flag Question"
            toolTip="When enabled, if the answer to this question is 'yes', the answer will show a red flag instead of a green check mark.
            Likewise, if the answer is 'no', the answer will show a green check mark.
            Use this when you want PINS AI Assistant to find something that would cause the requirement to be non-compliant."
            type="switch"
            useFormMethods={useFormMethods}
          />

          <Box mb={2}>
            <InputLabel required labelName="Related Requirements" />
            <SearchInputGroup h="40px" placeHolder="Search by Requirement Name" onChange={onSearchChange} />
          </Box>

          <Box maxH="300px" mb={4} overflowY="auto">
            <Box m={4}>
              <HStack
                cursor="pointer"
                data-cy={CypressIds.selectAllRequirementTypeLinkCheckboxes}
                p={2}
                spacing={4}
                onClick={e => {
                  if (e.target.closest('input[type="checkbox"]')) return;

                  const ans = fixedBlocks.every(block => block.isSelected);
                  handleCheckAll(!ans);
                }}
              >
                <Checkbox
                  isChecked={fixedBlocks.every(block => block.isSelected)}
                />
                <Text color="greyScale.700" textStyle="bodyMd">{checkAllText}</Text>
              </HStack>
              <Divider borderColor="greyScale.700" mt={4} />
              <Box maxH="500px" overflowY="auto">
                {fixedBlocks.map(block => {
                  const { requirementTemplateBlockProperties } = block;
                  const firstProperty = requirementTemplateBlockProperties[0];

                  const getCypressId = () => {
                    if (firstProperty?.requirementType === 'insurance_policy_type_property') {
                      return CypressIds.policyRequirementTypeQuestionLinkCheckbox(firstProperty?.requirement?.insurancePolicyTypeId);
                    }

                    return CypressIds.docRequirementTypeQuestionLinkCheckbox(firstProperty?.requirement?.id);
                  };

                  return (
                    <Box key={block.id} my={4}>
                      <HStack
                        background={block.isSelected ? colors.gradient.aiPrimaryOpaque : 'inherit'}
                        borderRadius="md"
                        cursor="pointer"
                        data-cy={getCypressId()}
                        p={2}
                        spacing={4}
                        onClick={e => {
                          if (e.target.closest('input[type="checkbox"]')) return;
                          handleCheckboxChange(block.id, !block.isSelected);
                        }}
                      >
                        <Checkbox
                          isChecked={block.isSelected}
                        />
                        <Text color="greyScale.700" textStyle="bodyMd">{firstProperty?.policyTypeDisplayName ?? firstProperty?.requirement?.displayName}</Text>
                      </HStack>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DrawerBody>

        <DrawerFooter px={0}>
          <Button
            data-cy={CypressIds.saveQuestionRequirementButton}
            h="48px"
            type="submit"
            variant="gradientAi"
            w="100%"
          >
            Save
          </Button>
        </DrawerFooter>
      </form>
    </Box>
  );
};

QuestionLinkTray.propTypes = {
  blueprint: RequirementTemplateBlueprintType.isRequired,
  question: PropTypes.object,
  currentRequirementTemplateBlocks: PropTypes.array
};

export default QuestionLinkTray;
